import { Link } from '@/components/general/Link/Link';
import { Box, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { QuestionMarkCircle } from '@/assets/theme/default/general/QuestionMarkCircle';
import { Currency } from '@/components/general/Currency/Currency';
import { WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { usePhrase } from '@/hooks';
import { NonTaxable } from '../../NonTaxable/NonTaxable';

interface AvailableBalanceProps {
    currencySymbol: string | undefined | null;
    currencySymbolPosition: string | undefined | null;
    currencyCode: string;
    balance: number | undefined;
    balanceNonTaxable: number | undefined;
}

export const AvailableBalance = ({
    currencySymbol,
    currencyCode,
    currencySymbolPosition,
    balance,
    balanceNonTaxable,
}: AvailableBalanceProps) => {
    const { t } = usePhrase();
    return (
        <Paper sx={{ mb: 2, p: 4 }}>
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexDirection: ['column', 'column', 'row'],
                }}
            >
                <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: [1, 1, 0] }}>
                    <Link href="/wallet-intro" passHref>
                        <Stack
                            direction="row"
                            component="span"
                            spacing={1}
                            sx={{
                                display: 'inline-flex',
                                flexWrap: 'nowrap',
                                alignItems: 'center',
                            }}
                        >
                            <QuestionMarkCircle />
                            <Typography component="span" variant="subtitle2">
                                {t(WALLET_PHRASES.HOW_DOES_IT_WORK)}
                            </Typography>
                        </Stack>
                    </Link>
                </Grid>

                <Grid>
                    <Grid
                        container
                        direction={['column', 'row']}
                        sx={{
                            alignItems: 'center',
                            justifyContent: ['center', 'initial'],
                        }}
                    >
                        <Grid sx={{ textAlign: ['center', 'right'] }}>
                            <Stack>
                                <Typography variant="h1">
                                    <Currency
                                        code={currencyCode}
                                        symbol={currencySymbol}
                                        position={currencySymbolPosition}
                                        amount={balance ?? 0}
                                    />
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    sx={(theme) => ({
                                        color: theme.colors.other.text.gray,
                                    })}
                                >
                                    {t(WALLET_PHRASES.YOUR_AVAILABLE_BALANCE)}
                                </Typography>
                                {!!balanceNonTaxable && (
                                    <Grid container>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        >
                                            <Currency
                                                code={currencyCode}
                                                symbol={currencySymbol}
                                                position={currencySymbolPosition}
                                                amount={balanceNonTaxable}
                                            />
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            sx={(theme) => ({
                                                ml: 0.5,
                                                color: theme.colors.other.text.gray,
                                            })}
                                        >
                                            {t(WALLET_PHRASES.OF_WHICH_IS)}
                                            <Box sx={{ display: 'inline-flex', ml: 0.75 }}>
                                                <NonTaxable />
                                            </Box>
                                        </Typography>
                                    </Grid>
                                )}
                            </Stack>
                        </Grid>
                        {!!balance && (
                            <Grid sx={{ ml: [0, 4] }}>
                                <Link href="/wallet/payment" passHref>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        sx={{
                                            display: 'inline-block',
                                            px: 4,
                                            mt: [1, 0],
                                            '&::first-letter': { textTransform: 'uppercase' },
                                        }}
                                    >
                                        {t(WALLET_PHRASES.REQUEST_A_PAYMENT)}
                                    </Button>
                                </Link>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};
